import {Configurator} from '@spartacus/product-configurator/rulebased';
import {compact, flatten} from 'lodash-es';

export const READ_ONLY_ATTRIBUTES = 'LIC_READONLY_ATTRIBUTES';
export const HIDDEN_ATTRIBUTES = 'LIC_HIDE_ATTRIBUTES';
export const TECHNICAL_GROUP_NAME = 'TECHNICAL';
export const LICENSE_MATERIAL_ATTRIBUTE = 'LIC_LICENSE_MAT';
export const QUANTITY_INCREMENT_ATTRIBUTE = 'LIC_QUANTITY_INCR';
export const LICENSE_SALES_UNIT_OF_MEASURE_ATTRIBUTE = 'LIC_DCP_SALES_UNIT';
export const MINIMUM_QUANTITY_ATTRIBUTE = 'LIC_MINIMUM_QUANTITY';
export const MAXIMUM_QUANTITY_ATTRIBUTE = 'LIC_MAXIMUM_QUANTITY';
export const ADDITIONAL_SERVICES_GROUP_NAME = '_ADD_SERV';
export const END_DATE_ATTRIBUTE = 'LIC_END_DATE';
export const END_DATE_STO_ATTRIBUTE = 'LIC_END_DATE_STO';
export const ENTITLEMENT_SET_ATTRIBUTE = 'LIC_ENTITLEMENT_SET';
export const TRANSACTION_TYPE_ATTRIBUTE = 'LIC_TRANSACTION_TYPE';
export const OPPORTUNITY_ID_ATTRIBUTE = 'LIC_OPPORTUNITY_ID';
export const END_CUSTOMER_ID_ATTRIBUTE = 'LIC_END_CUSTOMER_ID';
export const END_CUSTOMER_NAME_ATTRIBUTE = 'LIC_END_CUSTOMER_NAME';
export const BUSINESS_MODEL_ATTRIBUTE = 'LIC_BUSINESS_MODEL';
export const ALLOWED_TECHNICAL_ATTRIBUTES: string[] = ['LIC_EXTERNAL_PRODUCT'];
export const ALLOWED_ADDON_ATTRIBUTES: string[] = ['LIC_QUANTITY'];
const typePrefix = 'AttributeType_';

/**
 * finds and returns an attribute by attributeName in a given group
 *
 * @param group the group to search in
 * @param attributeName the attribute name to search for
 * @returns the found attribute or undefined if not found
 */
export const findAttributeInGroup = (group: Configurator.Group, attributeName: string) =>
  group?.attributes?.find((attribute) => attribute.name === attributeName);

export const getSelectedGroupAttributeValue = (
  configuration: Configurator.Configuration,
  groupName: string,
  attributeName: string,
): string | undefined =>
  configuration.flatGroups
    .find((group) => group.id.endsWith(groupName))
    ?.attributes?.find((a) => a.name === attributeName)?.selectedSingleValue;

/**
 * finds and returns the first attribute by attributeName in a given configurator group array
 *
 * @param groups the group array to search in
 * @param attributeName the attribute name to search for
 * @returns the first found attribute or undefined if not found
 */
export const findAttributeInConfigurationGroups = (
  groups: Configurator.Group[],
  attributeName: string,
): Configurator.Attribute | undefined => {
  const flatAttributes = compact(flatten(groups.map((g) => g.attributes)));
  return flatAttributes.find((a) => a?.name === attributeName);
};

/**
 * find and returns a group by name in a given configurator group array
 *
 * @param groups the group array to search in
 * @param groupName the group name to search for
 * @returns the first found group or undefined if not found
 */
export const findGroupByName = (groups: Configurator.Group[], groupName: string): Configurator.Group | undefined =>
  groups.find((group) => group?.name === groupName);

/**
 * determine if a group is the technical group
 *
 * @param group the group to check
 */
export const isTechnicalGroup = (group: Configurator.Group): boolean => !!group?.name?.includes(TECHNICAL_GROUP_NAME);

/**
 * determine if a group is not the technical group
 *
 * @param group the group to check
 */
export const isNotTechnicalGroup = (group: Configurator.Group): boolean => !group?.name?.includes(TECHNICAL_GROUP_NAME);

/**
 * determine if an attribute is a multivalues attribute
 *
 * @param attribute the attribute to check
 */
export const isMultiValueAttribute = (attribute: Configurator.Attribute): boolean =>
  !(attribute?.values === undefined || attribute?.values.length === 0);

export const getAddonGroup = (
  configuration: Configurator.Configuration,
  attributeName: string,
): Configurator.Group | undefined => {
  if (!(attributeName.includes('_MULTI') || attributeName.includes('_SINGL'))) {
    return undefined;
  }
  const materialName: string = attributeName.includes('_MULTI')
    ? attributeName.replace('_MULTI', '_MAT')
    : attributeName.replace('_SINGL', '_MAT');
  const materialAttribute: Configurator.Attribute | undefined = findAttributeInConfigurationGroups(
    configuration.flatGroups,
    materialName,
  );
  const material: string | undefined = materialAttribute?.values?.[0]?.name;
  if (!material?.length) {
    return undefined;
  }
  return findGroupByName(configuration.flatGroups, material);
};

export const getComponentKey = (attribute: Configurator.Attribute): string =>
  attribute.uiTypeVariation?.includes(Configurator.CustomUiTypeIndicator)
    ? typePrefix + attribute.uiTypeVariation
    : typePrefix + attribute.uiType;
